import {
  type CheckoutSplitPlanReadModel,
  isCheckoutSplitPlanReadModel,
  isOrderDetail,
  isPayCheckoutSplitShareRequest,
  isSaveCheckoutSplitPlanRequest,
  isTableSnapshot,
  isTableSnapshotList,
  type OrderDetail,
  type PayCheckoutSplitShareRequest,
  type SaveCheckoutSplitPlanRequest,
  type TableSnapshot,
} from "@billiard/contracts";

export interface CheckoutSplitRecoveryContext {
  tenantId: string;
  storeId: string;
  operatorId: string;
  sessionId: string;
}

export interface CheckoutSplitRecoveryBaseline {
  tenantId: string;
  storeId: string;
  baselineOrder: OrderDetail;
  baselineTable: TableSnapshot;
  baselinePlan: CheckoutSplitPlanReadModel | null;
}

export type CheckoutSplitRecoveryCommand =
  | {
      kind: "SAVE_PLAN";
      request: SaveCheckoutSplitPlanRequest;
      baseline: CheckoutSplitRecoveryBaseline;
    }
  | {
      kind: "PAY_SHARE";
      request: PayCheckoutSplitShareRequest;
      baseline: CheckoutSplitRecoveryBaseline & { baselinePlan: CheckoutSplitPlanReadModel };
    };

export type CheckoutSplitRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN" | "RETRY_ALLOWED";

export interface CheckoutSplitRecovery extends CheckoutSplitRecoveryContext {
  command: CheckoutSplitRecoveryCommand;
  state: CheckoutSplitRecoveryState;
}

export type CheckoutSplitRecoveryView =
  | { status: "NONE"; canRetry: false }
  | { status: "OWNED"; recovery: CheckoutSplitRecovery; canRetry: boolean }
  | { status: "CONTEXT_MISMATCH"; canRetry: false }
  | { status: "COMMAND_MISMATCH"; canRetry: false }
  | {
      status: "RECOVERY_STORAGE_UNAVAILABLE";
      canRetry: false;
      pendingOutcomeMayExist: boolean;
    };

export type CheckoutSplitAuthorityResolution =
  | { status: "EXECUTED" }
  | { status: "RETRY_ALLOWED"; recovery: CheckoutSplitRecovery }
  | { status: "STILL_UNKNOWN" }
  | { status: "FAIL_CLOSED" };

export interface CheckoutSplitRecoveryUiState {
  blocked: boolean;
  canRetry: boolean;
  needsAuthorityRead: boolean;
  message: string | null;
}

interface StorageAccess {
  browser: boolean;
  storage: Storage | null;
}

export const CHECKOUT_SPLIT_RECOVERY_STORAGE_KEY = "billiard-erp:checkout-split-recovery:v1";

const definiteNoEffectCodes = new Set([
  "AUTH_REQUIRED",
  "AUTHORIZATION_DEFAULT_DENY",
  "CHECKOUT_SPLIT_ALREADY_COMPLETED",
  "CHECKOUT_SPLIT_ORDER_CHANGED",
  "CHECKOUT_SPLIT_PLAN_NOT_FOUND",
  "CHECKOUT_SPLIT_PLAN_VERSION_CONFLICT",
  "CHECKOUT_SPLIT_SHARE_ALREADY_PAID",
  "CHECKOUT_SPLIT_SHARE_NOT_FOUND",
  "CHECKOUT_SPLIT_TOTAL_CHANGED",
  "CONDITIONAL_MODULE_DISABLED",
  "CSRF_REJECTED",
  "IDEMPOTENCY_COLLISION",
  "IDEMPOTENCY_KEY_REUSED",
  "MOBILE_SESSION_ACTIVATION_REQUIRED",
  "NATIVE_APP_VERSION_INVALID",
  "NATIVE_APP_VERSION_MISMATCH",
  "NATIVE_APP_VERSION_REQUIRED",
  "NATIVE_SECURE_SESSION_UNAVAILABLE",
  "NATIVE_SERVER_UPDATE_REQUIRED",
  "NATIVE_UPDATE_REQUIRED",
  "OFFLINE_AUTHORITY_UNAVAILABLE",
  "ONLINE_SERVICE_REQUIRED",
  "ORIGIN_REJECTED",
  "PAYMENT_METHOD_DISABLED",
  "REQUEST_VALIDATION_FAILED",
  "SESSION_NOT_OPEN",
]);

let pendingRecovery: CheckoutSplitRecovery | null = null;
let dispatchingRecovery: CheckoutSplitRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;
let pendingOutcomeMayExist = false;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isBoundedIdentifier(value: unknown, maximum = 256): value is string {
  return (
    typeof value === "string" &&
    value.length >= 1 &&
    value.length <= maximum &&
    value === value.trim()
  );
}

function contextMatches(
  recovery: CheckoutSplitRecovery,
  context: CheckoutSplitRecoveryContext,
): boolean {
  return (
    recovery.tenantId === context.tenantId &&
    recovery.storeId === context.storeId &&
    recovery.operatorId === context.operatorId &&
    recovery.sessionId === context.sessionId
  );
}

function isContext(value: unknown): value is CheckoutSplitRecoveryContext {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["tenantId", "storeId", "operatorId", "sessionId"]) &&
    isBoundedIdentifier(value.tenantId) &&
    isBoundedIdentifier(value.storeId) &&
    isBoundedIdentifier(value.operatorId) &&
    isBoundedIdentifier(value.sessionId, 128)
  );
}

function orderIdentity(order: OrderDetail): string {
  return order.orderNumber ?? order.id;
}

function baselineIsConsistent(
  baseline: unknown,
  context: CheckoutSplitRecoveryContext,
): baseline is CheckoutSplitRecoveryBaseline {
  if (
    !isRecord(baseline) ||
    !hasExactKeys(baseline, [
      "tenantId",
      "storeId",
      "baselineOrder",
      "baselineTable",
      "baselinePlan",
    ]) ||
    baseline.tenantId !== context.tenantId ||
    baseline.storeId !== context.storeId ||
    !isOrderDetail(baseline.baselineOrder) ||
    !isTableSnapshot(baseline.baselineTable) ||
    !isTableSnapshotList([baseline.baselineTable], context.tenantId, context.storeId)
  ) {
    return false;
  }
  const order = baseline.baselineOrder;
  const table = baseline.baselineTable;
  if (
    table.tenantId !== context.tenantId ||
    table.storeId !== context.storeId ||
    order.sessionId !== context.sessionId ||
    order.status !== "OPEN" ||
    order.billingState !== "ENDED" ||
    order.billingPausedAt !== null ||
    order.endedAt === null ||
    order.totalFen <= 0 ||
    order.refundedFen !== 0 ||
    order.refunds.length !== 0 ||
    table.tableId !== order.tableId ||
    table.tableNumber !== order.tableNumber ||
    table.state !== "IN_USE" ||
    table.sessionId !== context.sessionId ||
    table.sessionStartedAt !== order.startedAt ||
    table.billingState !== "ENDED" ||
    table.billingPausedAt !== null ||
    table.currentTotalFen !== order.totalFen
  ) {
    return false;
  }
  if (baseline.baselinePlan === null) {
    return order.paidFen === 0 && order.payments.length === 0;
  }
  const plan = baseline.baselinePlan;
  return (
    isCheckoutSplitPlanReadModel(plan) &&
    plan.sessionId === context.sessionId &&
    plan.orderId === order.id &&
    plan.orderNumber === orderIdentity(order) &&
    plan.businessDate === order.businessDate &&
    plan.totalFen === order.totalFen &&
    plan.paidFen === order.paidFen
  );
}

function commandIsConsistent(
  command: unknown,
  context: CheckoutSplitRecoveryContext,
): command is CheckoutSplitRecoveryCommand {
  if (!isRecord(command) || !hasExactKeys(command, ["kind", "request", "baseline"])) return false;
  if (!baselineIsConsistent(command.baseline, context)) return false;
  const baseline = command.baseline;
  if (command.kind === "SAVE_PLAN") {
    return (
      isSaveCheckoutSplitPlanRequest(command.request) &&
      command.request.expectedOrderId === baseline.baselineOrder.id &&
      command.request.expectedTotalFen === baseline.baselineOrder.totalFen &&
      command.request.expectedPlanVersion === (baseline.baselinePlan?.version ?? null) &&
      baseline.baselineOrder.paidFen === 0 &&
      baseline.baselineOrder.payments.length === 0 &&
      (baseline.baselinePlan === null || baseline.baselinePlan.status === "DRAFT")
    );
  }
  if (command.kind !== "PAY_SHARE" || !isPayCheckoutSplitShareRequest(command.request))
    return false;
  const plan = baseline.baselinePlan;
  if (plan === null) return false;
  const request = command.request as PayCheckoutSplitShareRequest;
  const share = plan.shares.find((candidate) => candidate.shareId === request.shareId);
  return (
    request.planId === plan.planId &&
    request.expectedPlanVersion === plan.version &&
    share?.status === "UNPAID" &&
    share.amountFen === request.expectedShareAmountFen
  );
}

function immutableRecovery(
  context: CheckoutSplitRecoveryContext,
  command: CheckoutSplitRecoveryCommand,
  state: CheckoutSplitRecoveryState,
): CheckoutSplitRecovery {
  const snapshot = structuredClone({ ...context, command, state }) as CheckoutSplitRecovery;
  const freezeDeep = (value: unknown): void => {
    if (!value || typeof value !== "object" || Object.isFrozen(value)) return;
    for (const child of Object.values(value)) freezeDeep(child);
    Object.freeze(value);
  };
  freezeDeep(snapshot);
  return snapshot;
}

function storedRecovery(value: unknown): CheckoutSplitRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery) ||
    !hasExactKeys(value.recovery, [
      "tenantId",
      "storeId",
      "operatorId",
      "sessionId",
      "command",
      "state",
    ])
  ) {
    return null;
  }
  const recovery = value.recovery;
  const context = {
    tenantId: recovery.tenantId,
    storeId: recovery.storeId,
    operatorId: recovery.operatorId,
    sessionId: recovery.sessionId,
  };
  if (
    !isContext(context) ||
    !commandIsConsistent(recovery.command, context) ||
    (recovery.state !== "CLAIMED" &&
      recovery.state !== "OUTCOME_UNKNOWN" &&
      recovery.state !== "RETRY_ALLOWED")
  ) {
    return null;
  }
  return immutableRecovery(context, recovery.command, recovery.state);
}

function recoveryEquals(left: CheckoutSplitRecovery, right: CheckoutSplitRecovery): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function sessionStorageAccess(): StorageAccess {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function readStoredRecovery(storage: Storage): CheckoutSplitRecovery | null {
  const encoded = storage.getItem(CHECKOUT_SPLIT_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStoredRecovery(recovery: CheckoutSplitRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    access.storage.setItem(
      CHECKOUT_SPLIT_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function hydrateStoredRecovery(): void {
  if (hydrated) return;
  hydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(CHECKOUT_SPLIT_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    pendingOutcomeMayExist = true;
    const stored = storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const restored =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStoredRecovery(restored)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = restored;
  } catch {
    recoveryUnavailable = true;
  }
}

function unavailableView(): CheckoutSplitRecoveryView {
  return {
    status: "RECOVERY_STORAGE_UNAVAILABLE",
    canRetry: false,
    pendingOutcomeMayExist,
  };
}

export function checkoutSplitRecoveryContext(
  operator: { tenantId: string; storeId: string; id: string },
  sessionId: string,
): CheckoutSplitRecoveryContext {
  return {
    tenantId: operator.tenantId,
    storeId: operator.storeId,
    operatorId: operator.id,
    sessionId,
  };
}

export function readCheckoutSplitRecovery(
  context: CheckoutSplitRecoveryContext,
): CheckoutSplitRecoveryView {
  hydrateStoredRecovery();
  if (recoveryUnavailable || !isContext(context)) return unavailableView();
  if (!pendingRecovery) return { status: "NONE", canRetry: false };
  if (!contextMatches(pendingRecovery, context)) {
    return { status: "CONTEXT_MISMATCH", canRetry: false };
  }
  return {
    status: "OWNED",
    recovery: pendingRecovery,
    canRetry: pendingRecovery.state === "RETRY_ALLOWED",
  };
}

export function claimCheckoutSplitRecovery(
  context: CheckoutSplitRecoveryContext,
  command: CheckoutSplitRecoveryCommand,
): CheckoutSplitRecoveryView {
  const current = readCheckoutSplitRecovery(context);
  if (current.status === "OWNED") {
    return JSON.stringify(current.recovery.command) === JSON.stringify(command)
      ? current
      : { status: "COMMAND_MISMATCH", canRetry: false };
  }
  if (current.status !== "NONE") return current;
  if (!isContext(context) || !commandIsConsistent(command, context)) {
    recoveryUnavailable = true;
    return unavailableView();
  }
  const recovery = immutableRecovery(context, command, "CLAIMED");
  if (!saveStoredRecovery(recovery)) {
    recoveryUnavailable = true;
    return unavailableView();
  }
  pendingRecovery = recovery;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery, canRetry: false };
}

function verifyRecovery(
  recovery: CheckoutSplitRecovery,
  context: CheckoutSplitRecoveryContext,
): boolean {
  hydrateStoredRecovery();
  if (recoveryUnavailable || pendingRecovery !== recovery || !contextMatches(recovery, context)) {
    return false;
  }
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) {
    recoveryUnavailable = true;
    return false;
  }
  try {
    const stored = readStoredRecovery(access.storage);
    if (!stored || !recoveryEquals(stored, recovery)) {
      recoveryUnavailable = true;
      return false;
    }
    return true;
  } catch {
    recoveryUnavailable = true;
    return false;
  }
}

export function beginCheckoutSplitDispatch(
  recovery: CheckoutSplitRecovery,
  context: CheckoutSplitRecoveryContext,
): boolean {
  if (
    dispatchingRecovery ||
    recovery.state === "OUTCOME_UNKNOWN" ||
    !verifyRecovery(recovery, context)
  ) {
    return false;
  }
  dispatchingRecovery = recovery;
  return true;
}

export function finishCheckoutSplitDispatch(
  recovery: CheckoutSplitRecovery,
  context: CheckoutSplitRecoveryContext,
): boolean {
  if (dispatchingRecovery !== recovery || !contextMatches(recovery, context)) return false;
  dispatchingRecovery = null;
  return true;
}

export function markCheckoutSplitOutcomeUnknown(
  recovery: CheckoutSplitRecovery,
  context: CheckoutSplitRecoveryContext,
): boolean {
  if (!verifyRecovery(recovery, context)) return false;
  const unknown = immutableRecovery(context, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStoredRecovery(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  pendingOutcomeMayExist = true;
  return true;
}

export function clearCheckoutSplitRecovery(
  recovery: CheckoutSplitRecovery,
  context: CheckoutSplitRecoveryContext,
): boolean {
  if (!verifyRecovery(recovery, context)) return false;
  const access = sessionStorageAccess();
  if (access.browser) {
    if (!access.storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      access.storage.removeItem(CHECKOUT_SPLIT_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(CHECKOUT_SPLIT_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
        return false;
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  if (dispatchingRecovery === recovery) dispatchingRecovery = null;
  pendingRecovery = null;
  pendingOutcomeMayExist = false;
  return true;
}

function planMatchesSavedCommand(
  plan: CheckoutSplitPlanReadModel,
  command: Extract<CheckoutSplitRecoveryCommand, { kind: "SAVE_PLAN" }>,
  sessionId: string,
): boolean {
  const baselinePlan = command.baseline.baselinePlan;
  return (
    plan.sessionId === sessionId &&
    plan.orderId === command.request.expectedOrderId &&
    plan.totalFen === command.request.expectedTotalFen &&
    plan.version === (baselinePlan?.version ?? 0) + 1 &&
    plan.status === "DRAFT" &&
    plan.paidFen === 0 &&
    plan.allocationMode === command.request.allocationMode &&
    plan.shares.length === command.request.shares.length &&
    command.request.shares.every((draft, index) => {
      const share = plan.shares[index];
      return (
        share?.allocationOrder === index + 1 &&
        share.shareKey === draft.shareKey &&
        share.payerLabel === draft.payerLabel &&
        share.amountFen === draft.amountFen &&
        share.otherAmountFen === draft.otherAmountFen &&
        JSON.stringify(share.itemAllocations) === JSON.stringify(draft.itemAllocations) &&
        share.status === "UNPAID" &&
        share.paymentId === null &&
        share.paymentMethod === null &&
        share.paidAt === null
      );
    })
  );
}

function commandWasExecuted(
  recovery: CheckoutSplitRecovery,
  authoritativePlan: CheckoutSplitPlanReadModel | null,
): boolean {
  if (!authoritativePlan) return false;
  if (recovery.command.kind === "SAVE_PLAN") {
    return planMatchesSavedCommand(authoritativePlan, recovery.command, recovery.sessionId);
  }
  const request = recovery.command.request;
  const share = authoritativePlan.shares.find((candidate) => candidate.shareId === request.shareId);
  return (
    authoritativePlan.sessionId === recovery.sessionId &&
    authoritativePlan.planId === request.planId &&
    share?.status === "PAID" &&
    share.amountFen === request.expectedShareAmountFen &&
    share.paymentMethod === request.method &&
    share.paymentId !== null &&
    share.paidAt !== null
  );
}

function commandDefinitelyDidNotExecute(
  recovery: CheckoutSplitRecovery,
  authoritativePlan: CheckoutSplitPlanReadModel | null,
): boolean {
  const baselinePlan = recovery.command.baseline.baselinePlan;
  return JSON.stringify(authoritativePlan) === JSON.stringify(baselinePlan);
}

export function reconcileCheckoutSplitAuthority(
  recovery: CheckoutSplitRecovery,
  context: CheckoutSplitRecoveryContext,
  authoritativePlan: CheckoutSplitPlanReadModel | null,
): CheckoutSplitAuthorityResolution {
  if (
    recovery.state !== "OUTCOME_UNKNOWN" ||
    !verifyRecovery(recovery, context) ||
    (authoritativePlan !== null && !isCheckoutSplitPlanReadModel(authoritativePlan))
  ) {
    return { status: "FAIL_CLOSED" };
  }
  if (commandWasExecuted(recovery, authoritativePlan)) {
    return clearCheckoutSplitRecovery(recovery, context)
      ? { status: "EXECUTED" }
      : { status: "FAIL_CLOSED" };
  }
  if (!commandDefinitelyDidNotExecute(recovery, authoritativePlan)) {
    return { status: "STILL_UNKNOWN" };
  }
  const retryable = immutableRecovery(context, recovery.command, "RETRY_ALLOWED");
  if (!saveStoredRecovery(retryable)) {
    recoveryUnavailable = true;
    return { status: "FAIL_CLOSED" };
  }
  pendingRecovery = retryable;
  return { status: "RETRY_ALLOWED", recovery: retryable };
}

export function checkoutSplitOutcomeIsUnknown(error: unknown): boolean {
  return !(
    isRecord(error) &&
    typeof error.code === "string" &&
    definiteNoEffectCodes.has(error.code)
  );
}

export function checkoutSplitRecoveryUiState(
  view: CheckoutSplitRecoveryView,
): CheckoutSplitRecoveryUiState {
  if (view.status === "NONE") {
    return { blocked: false, canRetry: false, needsAuthorityRead: false, message: null };
  }
  if (view.status === "OWNED") {
    if (view.recovery.state === "OUTCOME_UNKNOWN") {
      return {
        blocked: true,
        canRetry: false,
        needsAuthorityRead: true,
        message: "上一笔分账结果尚未确认，请先读取当前分账结果。",
      };
    }
    return {
      blocked: !view.canRetry && view.recovery.state !== "CLAIMED",
      canRetry: view.canRetry,
      needsAuthorityRead: false,
      message: view.canRetry ? "已确认上一笔没有执行，可以使用原请求号重试。" : null,
    };
  }
  return {
    blocked: true,
    canRetry: false,
    needsAuthorityRead: false,
    message:
      view.status === "CONTEXT_MISMATCH"
        ? "这笔分账属于其他员工、门店或台局，当前页面不能继续操作。"
        : view.status === "COMMAND_MISMATCH"
          ? "待确认的分账内容与当前操作不同，不能替换原请求。"
          : "浏览器无法安全读取分账恢复记录，已停止新的分账操作。",
  };
}

export function resetCheckoutSplitRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
  const access = sessionStorageAccess();
  try {
    access.storage?.removeItem(CHECKOUT_SPLIT_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup also resets the in-memory fail-closed state.
  }
}

export function resetCheckoutSplitRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
}
